import type { FC, ReactNode } from 'react'
import type { HintKind } from '@/types'

import { Blocks } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePreferences } from '@/components/generals/providers'
import { HINTS, HINT_KIND_META } from '@/constants/confluence'

const KIND_STYLES: Record<HintKind, string> = {
    native: 'border-success/40 text-success-strong',
    addon: 'border-warning/60 text-warning-strong',
    alternative: 'border-input text-muted-foreground'
}

const KIND_DOTS: Record<HintKind, string> = {
    native: 'bg-success',
    addon: 'bg-warning',
    alternative: 'bg-subtle'
}

interface ConfluenceHintProps {
    id: string
    placement?: 'corner' | 'inline'
    className?: string
}

/** Implementation annotation, visible only while the Confluence guide mode is switched on. */
export const ConfluenceHint: FC<ConfluenceHintProps> = ({
    id,
    placement = 'corner',
    className
}): ReactNode => {
    const { hints } = usePreferences()
    const items = HINTS[id]

    if (!hints || !items?.length) return null

    return (
        <div
            role='note'
            aria-label='راهنمای پیاده‌سازی در Confluence'
            className={cn(
                'flex flex-wrap items-center gap-1 animate-in fade-in zoom-in-95 duration-200',
                placement === 'corner' ? 'pointer-events-auto absolute -top-3 end-3 z-20 max-w-[calc(100%-1.5rem)] justify-end' : 'relative',
                className
            )}
        >
            <span className='flex h-6 items-center gap-1 rounded-full bg-navy px-2 text-[11px] font-semibold text-white shadow-raised' dir='ltr'>
                <Blocks className='size-3' aria-hidden='true' />
                Confluence
            </span>

            {items.map(item => (
                <span
                    key={item.name}
                    dir='auto'
                    title={`${HINT_KIND_META[item.kind].title}: ${HINT_KIND_META[item.kind].description}`}
                    className={cn('flex h-6 items-center gap-1.5 rounded-full border bg-card px-2 text-[11px] font-medium shadow-card', KIND_STYLES[item.kind])}
                >
                    <span className={cn('size-1.5 shrink-0 rounded-full', KIND_DOTS[item.kind])} aria-hidden='true' />
                    {item.name}
                </span>
            ))}
        </div>
    )
}
